import campaignsService from '@/api/campaignsService'
import channelService from '@/api/channelService'
import { CHANNEL_FORMATS, CHANNEL_LABELS, CAMPAIGN_STATUSES, CHANNEL_PRODUCT_TYPES, CREATIVITY_FORMATS, CHANNEL_OPTIONS, TEMPLATE_UPDATE_TIME_ERROR } from '@/utils/campaignOptions'
import { TARGET_TYPES } from '@/utils/adminOptions'
import layerService from '@/api/layerService'
import { mapMutations, mapActions, mapState } from 'vuex'
import { MODULE_NAME as MODULE_NAME_MAP, MUTATIONS as MUTATIONS_MAP } from '@/store/map/constants'
import { MODULE_NAME as MODULE_NAME_LOC, MUTATIONS as MUTATIONS_LOC, ACTIONS as ACTIONS_LOC } from '@/store/locations/constants'
import { multiPoint, bbox as getBbox } from '@turf/turf'
import templatesService from '@/api/templatesService'
import authService, { USER_ROLES } from '@/api/authService'
import PointRadiusLayer from '@/api/PointRadiusLayer'
import moment from 'moment'
import { ADVERT_TYPES, advertIconUrl, advertSquareIconUrl, shopIconUrl, poiIconUrl, productPoiIconUrl } from '@/utils/mapUtils'
import { sanitizeChannelProps } from '@/utils/channelUtils'
import { validateUrl } from '@/utils/formValidators'

const newTemplate = () => ({ id: 'new', name: 'Sin plantilla (nuevo)' })
const allOption = () => ({ id: 'all', label: 'Todos los Códigos Postales', name: 'Todos los Códigos Postales', code: 'Todos los Códigos Postales' })
const formatOptions = Object.entries(CHANNEL_FORMATS).map(([key, value]) => ({ id: key, name: value.name }))

const MULTIPRODUCT_LIMIT_MIN = 2
const MULTIPRODUCT_LIMIT_MAX = 8

function matchYoutubeUrl (url) {
  const p = /^(?:https?:\/\/)?(?:m\.|www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/
  if (url && url.match(p)) {
    return url.match(p)[1]
  }
  return false
}

function isValidHttpUrl (string) {
  let url

  try {
    url = new URL(string)
  } catch (e) {
    return false
  }

  return url.protocol === 'https:'
}

const defaultChannel = () => ({
  id: null,
  format: null,
  budget: null,
  template: null,
  products: [],
  target: null,
  copy: null,
  start_date: null,
  finish_date: null,
  comment: null,
  zones: [],
  adverts: [],
  pois: [],
  shops: [],
  saveAsTemplate: false,
  newTemplateName: null,
  direction: CHANNEL_PRODUCT_TYPES.REDIRECT.value,
  creativity_url: null,
  opening_creativity_url: null,
  closing_creativity_url: null,
  files: [],
  opening_files: [],
  closing_files: [],
  impacts: null,
  coverage: null,
  geo_template_id: null,
  purchase_type: null,
  video_length: null,
  video_url: null
})

const ZIPCODE_COLOR = [22, 150, 157, 25]
const ZIPCODE_HOVER_COLOR = [22, 150, 157, 75]
const ZIPCODE_SELECTED_COLOR = [22, 150, 157, 140]
const ZIPCODE_BORDER_COLOR = [22, 150, 157, 255]

export default {
  name: 'ChannelConfig',
  mixins: [],
  components: {
    MapComponent: () => import(/* webpackChunkName: "MapComponent" */ '@/components/map-component/MapComponent.vue'),
    ChannelSidebar: () => import(/* webpackChunkName: "ChannelSidebar" */ '@/components/channel-sidebar/ChannelSidebar.vue'),
    ChannelHeader: () => import(/* webpackChunkName: "ChannelHeader" */ '@/components/channel-header/ChannelHeader.vue')
  },
  props: {},
  data () {
    return {
      shopLayerId: 'campaign_channel_shop',
      advertsLayerId: 'campaign_channel_adverts',
      poisLayerId: 'campaign_channel_pois',
      buffersLayerId: 'campaign_channel_buffers',
      zoneLayersId: 'campaign_channel_zones',
      loading: false,
      mapLoaded: false,
      campaign: undefined,
      selectedAreaTab: null,
      boxSelectionEnabled: false,
      dragStart: null,
      dragCurrent: null,
      channel: defaultChannel(),
      hoveredZipcodeId: null,
      kpisDirty: false,
      areasReady: false,
      authData: authService.authData,
      templateUpdateError: null,
      isChannelChanged: false
    }
  },
  computed: {
    ...mapState(MODULE_NAME_LOC, [ 'viewportFilter', 'pois', 'zones', 'adverts' ]),
    campaignid () {
      return this.$route.params.campaignid
    },
    channelid () {
      return this.$route.params.channelid
    },
    type () {
      return this.$route.params.channeltype
    },
    productsLengthOk () {
      const len = this.channel.products.length
      return len >= MULTIPRODUCT_LIMIT_MIN && len <= MULTIPRODUCT_LIMIT_MAX
    },
    dateRangeOk () {
      const minTime = new Date(this.campaign.start_date).getTime()
      const maxTime = new Date(this.campaign.finish_date).getTime()
      const startTime = moment(this.channel.start_date).endOf('day').toDate().getTime()
      const endTime = moment(this.channel.finish_date).startOf('day').toDate().getTime()
      return startTime >= minTime && endTime <= maxTime
    },
    formatValid () {
      let formatCheck = false
      if (this.channel.direction === CHANNEL_PRODUCT_TYPES.REDIRECT.value) {
        formatCheck = !!this.channel.format
      }
      if (this.channel.direction === CHANNEL_PRODUCT_TYPES.DIRECT.value) {
        formatCheck = this.channel.creativity_url && this.channel.files.every(f => f.format)
      }
      if (this.channel.direction === CHANNEL_PRODUCT_TYPES.HYBRID.value) {
        const hasOpeningFiles = this.channel.opening_files.length > 0
        const hasClosingFiles = this.channel.closing_files.length > 0
        const hasOpeningURL = !!this.channel.opening_creativity_url
        const hasClosingURL = !!this.channel.closing_creativity_url
        const openingFilesValid = this.channel.opening_files.every(f => f.format)
        const closingFilesValid = this.channel.closing_files.every(f => f.format)

        const openingValid = hasOpeningFiles && hasOpeningURL && openingFilesValid
        const closingValid = hasClosingFiles && hasClosingURL && closingFilesValid

        formatCheck = openingValid || closingValid
      }

      return formatCheck
    },
    productUrlCheck () {
      return this.channel.products.every(p => p.url_overriden ? isValidHttpUrl(p.url) : true)
    },
    youtubeUrlCheck () {
      return matchYoutubeUrl(this.channel.video_url)
    },
    formValid () {
      const isMultiProduct = this.channel.format && (
        this.channel.format.id === CHANNEL_FORMATS.FACEBOOK_CARROUSEL.key ||
        this.channel.format.id === CHANNEL_FORMATS.MOBILE_CARROUSEL.key
      )
      const productCheck = isMultiProduct
        ? this.productsLengthOk
        : this.channel.products.length > 0
      const budgetCheck = this.channel.budget || this.channel.budget === 0

      // Youtube
      if (this.isYoutube) {
        const isValidYoutubeForm = !!(
          this.channel.video_url &&
          this.youtubeUrlCheck &&
          this.channel.video_length &&
          this.areas.length &&
          this.dateRangeOk &&
          this.channel.target
        )

        return isValidYoutubeForm
      }

      const isValidTarget = !!(
        this.channel.target &&
        (
          (this.channel.target.type === TARGET_TYPES.FIRST_PARTY && Array.isArray(this.channel.target.identifiers) && this.channel.target.identifiers.length > 0) ||
          (this.channel.target.type === TARGET_TYPES.THIRD_PARTY && (this.channel.target.gender_ages || this.channel.target.interests))
        )
      ) || this.channel.target === null
      // URI validation
      const urls = [this.channel.opening_creativity_url, this.channel.closing_creativity_url, this.channel.creativity_url]

      if (urls.some(url => url && !validateUrl(url))) {
        return false
      }

      const baseCheck = !!(
        this.formatValid &&
        budgetCheck &&
        productCheck &&
        this.productUrlCheck &&
        this.dateRangeOk &&
        isValidTarget &&
        this.areas.length
      )
      return this.channel.saveAsTemplate
        ? baseCheck && !!this.channel.newTemplateName
        : baseCheck
    },
    channelZones () {
      return this.channel.zones || []
    },
    allZonesSelected () {
      return this.channelZones.length === 1 && this.channelZones[0].id === 'all'
    },
    allPOIsSelected () {
      return this.channel.pois.length === 1 && this.channel.pois[0].id === 'all'
    },
    readonly () {
      const { CENTRAL_ACCEPTED, CENTRAL_REJECTED, AGENCY_CONFIRMED, FINISHED, ACTIVE } = CAMPAIGN_STATUSES
      return this.channel.id && [CENTRAL_ACCEPTED, CENTRAL_REJECTED, AGENCY_CONFIRMED, FINISHED, ACTIVE].indexOf(this.campaign.status) !== -1
    },
    isServiceProduct () {
      return this.channel.direction === CHANNEL_PRODUCT_TYPES.DIRECT.value
    },
    isInterest () {
      return this.type === CHANNEL_OPTIONS.INTEREST
    },
    areas () {
      if (!this.selectedAreaTab) {
        return []
      }
      const entity = this.selectedAreaTab.entity
      if (entity === 'zone') {
        if (this.isInterest || this.isYoutube) {
          return [allOption()]
        }
        return this.channel.zones
      }
      if (entity === 'center') {
        if (this.campaign.shops.length === 1) {
          return this.campaign.shops
        }
        return this.channel.shops
      }
      if (entity === 'poi') {
        return this.channel.pois
      }
      if (entity === 'advert') {
        return this.channel.adverts
      }
      return []
    },
    hasBoxSelection () {
      return this.dragStart && this.dragCurrent
    },
    boxSelectionStyles () {
      if (!this.hasBoxSelection) {
        return { display: 'none' }
      }
      const minX = Math.min(this.dragStart.x, this.dragCurrent.x)
      const minY = Math.min(this.dragStart.y, this.dragCurrent.y)
      const maxX = Math.max(this.dragStart.x, this.dragCurrent.x)
      const maxY = Math.max(this.dragStart.y, this.dragCurrent.y)
      return {
        display: 'block',
        transform: `translate(${minX}px, ${minY}px)`,
        width: `${maxX - minX - 12}px`,
        height: `${maxY - minY - 12}px`
      }
    },
    productUrl () {
      return this.channel.products[0] && this.channel.products[0].url
    },
    isAgency () {
      return this.authData.user.role === USER_ROLES.AGENCY
    },
    isAdmin () {
      return this.authData.user.role === USER_ROLES.CENTRAL_ADMIN ||
        this.authData.user.role === USER_ROLES.ROOT
    },
    isYoutube () {
      return this.type === CHANNEL_OPTIONS.YOUTUBE
    }
  },
  watch: {
    'channel.budget': function (newBudget, oldBudget) {
      if (this.campaign) {
        this.kpisDirty = true
      }
    },
    'channel.target': function (newt, oldt) {
      if (this.channel.id && newt && oldt) {
        this.kpisDirty = true
      }
    },
    areas: function (newa, olda) {
      if (this.areasReady && newa !== olda) {
        this.kpisDirty = true
      }
    },
    'channel.products': function () {
      this.channel.creativity_url = this.productUrl || ''
      if (layerService.hasLayer(this.poisLayerId)) {
        this.updatePoiLayer()
      }
    },
    'channel.zones': function (zones) {
      if (Array.isArray(zones) && layerService.hasLayer(this.zoneLayersId)) {
        this.updateZipcodeLayer()
      }
    },
    'channel.pois': function (pois) {
      if (Array.isArray(pois) && layerService.hasLayer(this.poisLayerId)) {
        this.updatePoiLayer()
      }
    },
    'channel.shops': function (shops) {
      if (Array.isArray(shops) && layerService.hasLayer(this.shopLayerId)) {
        this.updateShopLayer()
      }
    },
    'channel.adverts': function (adverts) {
      if (Array.isArray(adverts) && layerService.hasLayer(this.advertsLayerId)) {
        this.updateAdvertLayer()
      }
    },
    'channel.purchase_type': function (purchase) {
      this.channel.purchase_type = purchase
      this.kpisDirty = true
    },
    'channel.video_length': function (duration) {
      this.channel.video_length = duration
    },
    'channel.video_url': function (url) {
      this.channel.video_url = url
    },
    'selectedAreaTab': function (tab, oldtab) {
      if (!(tab && tab.entity)) {
        return
      }
      if (oldtab && tab.entity === oldtab.entity && tab.radius === oldtab.radius) {
        return
      }
      if (this.boxSelectionEnabled && tab.entity !== 'zone') {
        this.disableBoxSelection()
      }
      if (layerService.hasLayer(this.poisLayerId)) {
        this.updatePoiLayer()
      }
      if (layerService.hasLayer(this.shopLayerId)) {
        this.updateShopLayer()
      }
      if (layerService.hasLayer(this.zoneLayersId)) {
        this.updateZipcodeLayer()
      }
      if (layerService.hasLayer(this.advertsLayerId)) {
        this.updateAdvertLayer()
      }
    },
    hoveredZipcodeId () {
      this.updateZipcodeLayer()
    }
  },
  async mounted () {
    try {
      const promises = [
        campaignsService.getCampaign({ id: this.campaignid }),
        this.channelid !== 'new' && channelService.getChannel({ id: this.channelid, type: this.type })
      ]
      const [campaign, channel] = await Promise.all(promises)

      if (
        channel.type === CHANNEL_OPTIONS.FACEBOOK &&
        [CAMPAIGN_STATUSES.AGENCY_CONFIRMED, CAMPAIGN_STATUSES.ACTIVE, CAMPAIGN_STATUSES.FINISHED].some(s => campaign.status === s) &&
        (!channel.credentials || !channel.credentials.fb_campaign_id) &&
        (this.isAgency || this.isAdmin)
      ) {
        this.$router.replace({ path: `/campaigns/${this.campaignid}?tab=4` })
      }

      if (
        channel.type === CHANNEL_OPTIONS.INSTAGRAM &&
        [CAMPAIGN_STATUSES.AGENCY_CONFIRMED, CAMPAIGN_STATUSES.ACTIVE, CAMPAIGN_STATUSES.FINISHED].some(s => campaign.status === s) &&
        (!channel.credentials || !channel.credentials.ig_campaign_id) &&
        (this.isAgency || this.isAdmin)
      ) {
        this.$router.replace({ path: `/campaigns/${this.campaignid}?tab=4` })
      }

      let efficiency = {}
      try {
        efficiency = await Promise.resolve(this.channelid !== 'new' && channelService.getChannelEfficiency(this.channelid, this.type))
      } catch (err) {
        // eslint-disable-next-line
        console.error('[channel-config.js] Error loading efficiency data for channel config', err)
      }
      if (channel) {
        if (channel.zones.length > 0) {
          channel.zones.sort((a, b) => {
            return Number(a.code) - Number(b.code)
          })
        }
        if (this.productUrl) {
          channel.creativity_url = this.productUrl
        }
        if (channel.template && !channel.products) {
          channel.products = channel.template.products
        }
        if (channel.template && !channel.target) {
          channel.target = channel.template.target
        }
        channel.template = channel.template || newTemplate()
        channel.pois = channel.pois.map(s => ({ ...s.customer_region, radius: s.radius }))
        channel.shops = channel.shops.map(s => ({ ...s.customer_region, radius: s.radius }))
        channel.adverts = channel.adverts.map(s => ({ ...s.customer_region, radius: s.radius }))
        channel.format = formatOptions.find(f => f.id === channel.selected_format)
        if (Array.isArray(channel.creativity)) {
          channel.files = channel.creativity.map((c, i) => ({
            url: c.creativity_url,
            format: Object.values(CREATIVITY_FORMATS).find(f => f.id === c.format),
            file: {},
            fileID: c.id
          }))
          if (channel.files.length > 0) {
            channel.creativity_url = channel.creativity[0].url
          }
        }

        this.channel = {
          ...this.channel,
          ...sanitizeChannelProps(channel),
          ...efficiency
        }
      }
      this.channel.start_date = new Date(this.channel.start_date || campaign.start_date)
      this.channel.finish_date = new Date(this.channel.finish_date || campaign.finish_date)
      this.$nextTick(() => {
        this.campaign = campaign
      })
    } catch (err) {
      // eslint-disable-next-line
      console.error('[channel-config.js] Error loading data for channel config', err)
    }
  },
  beforeDestroy () {
    this[MUTATIONS_MAP.RESET_VIEWSTATE]()
    this[MUTATIONS_LOC.CLEAR_LOCATIONS]()
    layerService.removeLayer(this.shopLayerId)
    layerService.removeLayer(this.advertsLayerId)
    layerService.removeLayer(this.buffersLayerId)
    layerService.removeLayer(this.poisLayerId)
    layerService.removeLayer(this.zoneLayersId)
    this.disableBoxSelection()
  },
  methods: {
    ...mapMutations(MODULE_NAME_MAP, [ MUTATIONS_MAP.SET_VIEWSTATE, MUTATIONS_MAP.RESET_VIEWSTATE, MUTATIONS_MAP.CENTER_BBOX ]),
    ...mapMutations(MODULE_NAME_LOC, [ MUTATIONS_LOC.SET_VIEWPORT_DATA, MUTATIONS_LOC.CLEAR_LOCATIONS ]),
    ...mapActions(MODULE_NAME_LOC, Object.values(ACTIONS_LOC)),
    async onMapLoaded () {
      const shopids = this.campaign.shops.map(s => s.id)
      if (shopids.length > 0) {
        await Promise.all([
          this[ACTIONS_LOC.FETCH_ADVERTS]({ shop_related__in: shopids, query: 1, reset: true, ordering: 'advert_id,address', date: this.campaign.created_at }),
          this[ACTIONS_LOC.FETCH_POIS]({ page_size: 10000, in_shop_influence_area: shopids, query: 1, reset: true, ordering: 'name' }),
          this[ACTIONS_LOC.FETCH_ZONES]({ in_shop_influence_area: shopids, query: 1, reset: true })
        ])
        await this.loadMap()
      }
      this.mapLoaded = true
    },
    async loadMap () {
      if (this.adverts.query) {
        await this.updateAdvertLayer()
      }
      if (this.pois.query) {
        await this.updatePoiLayer()
      }
      if (this.zones.query) {
        await this.updateZipcodeLayer()
      }
      return this.updateShopLayer()
    },
    centerMap (entity) {
      if (this.selectedAreaTab && this.selectedAreaTab.entity === entity) {
        return
      }

      let points = []
      // TODO: include lat/lng in the queries or find a way to convert the geoms
      // if (entity === 'poi') {
      //   points = this.viewportFilter.pois.map(d => d.geometry.coordinates)
      // }
      // if (entity === 'advert') {
      //   points = this.viewportFilter.adverts.map(d => d.geometry.coordinates)
      // }
      // if (entity === 'zone') {
      //   points = this.viewportFilter.zones.map(d => [d.properties.lng, d.properties.lat])
      // }

      const data = this.campaign.shops.map(p => [p.lng, p.lat]).concat(points)

      const geojson = multiPoint(data)
      const [minX, minY, maxX, maxY] = getBbox(geojson)
      this[MUTATIONS_MAP.CENTER_BBOX]({ bbox: [[minX, minY], [maxX, maxY]], maxZoom: 13 })
    },
    toggleLayer (entity) {
      if (this.selectedAreaTab && this.selectedAreaTab.entity === entity) {
        return
      }
      const mapping = {
        'advert': this.advertsLayerId,
        'poi': this.poisLayerId,
        'zone': this.zoneLayersId
      }
      for (const key in mapping) {
        if (entity === key) {
          layerService.showLayer(mapping[key])
        } else {
          layerService.hideLayer(mapping[key])
        }
      }
    },
    async updateShopLayer () {
      if (layerService.hasLayer(this.shopLayerId)) {
        layerService.updateLayer(this.shopLayerId, {
          updateTriggers: {
            getRadius: [this.channel.shops, this.selectedAreaTab]
          }
        })
      } else {
        layerService.addLayer({
          id: this.shopLayerId,
          data: this.campaign.shops,
          layerType: PointRadiusLayer,
          pickable: true,
          getFillColor: [22, 150, 157, 51],
          getLineColor: [22, 150, 157],
          getRadius: d => {
            const tab = this.selectedAreaTab || { radius: 0 }
            if (tab.entity !== 'center') {
              return 0
            }
            if (this.campaign.shops.length < 2) {
              return tab.radius
            }
            const selected = this.channel.shops.some(p => p.id === d.id)
            return selected ? tab.radius : 0
          },
          getPosition: d => [d.lng, d.lat],
          getIcon: () => ({
            width: 40,
            height: 40,
            url: shopIconUrl
          }),
          getTooltip ({ object }) {
            const text = `${object.name}`
            const style = {
              'background': 'var(--colorNegative)',
              color: 'var(--colorHigh)'
            }
            return { text, style, className: 'map-popup-white' }
          },
          updateTriggers: {
            getRadius: [this.channel.shops, this.selectedAreaTab]
          },
          onClick: this.readonly ? undefined : ({ object }) => {
            const tab = this.selectedAreaTab || {}
            if (tab.entity !== 'center' || this.campaign.shops.length < 2) {
              return
            }
            if (this.channel.shops.some(p => p.id === object.id)) {
              this.channel.shops = this.channel.shops.filter(s => s.id !== object.id)
            } else {
              this.channel.shops = this.channel.shops.concat(object)
            }
          }
        })
      }
    },
    getPoiCategories (poi) {
      let categories = []

      if (poi.category_ids) {
        categories = poi.category_ids.split(',').map(c => Number(c))
      }

      return categories && categories.length ? categories : [Number(poi.category)]
    },
    async updatePoiLayer () {
      if (layerService.hasLayer(this.poisLayerId)) {
        layerService.updateLayer(this.poisLayerId, {
          updateTriggers: {
            getIcon: [this.channel.products],
            getRadius: [this.channel.pois, this.selectedAreaTab]
          }
        })
      } else {
        await layerService.addSQLLayer({
          id: this.poisLayerId,
          sql: this.pois.query,
          sublayer: PointRadiusLayer,
          pickable: true,
          getFillColor: [22, 150, 157, 51],
          getLineColor: [22, 150, 157],
          iconSizeScale: 36,
          getRadius: d => {
            const id = d.properties.cartodb_id
            const selected = this.selectedAreaTab && this.channel.pois.some(p => p.id === id)
            return ((selected || this.allPOIsSelected) && this.selectedAreaTab.radius) || 0
          },
          getPosition: d => d.geometry.coordinates,
          getIcon: d => {
            const categories = this.getPoiCategories(d.properties)
            const isProductPoi = categories && this.channel.products.some(p => categories.includes(Number(p.category)))
            return {
              width: 40,
              height: 40,
              url: isProductPoi ? productPoiIconUrl : poiIconUrl
            }
          },
          getTooltip ({ object }) {
            const text = `${object.properties.name}`
            const style = {
              'background': 'var(--colorNegative)',
              color: 'var(--colorHigh)'
            }
            return { text, style, className: 'map-popup-white' }
          },
          updateTriggers: {
            getIcon: [this.channel.products],
            getRadius: [this.channel.pois, this.selectedAreaTab]
          },
          onClick: this.readonly ? undefined : ({ object }) => {
            const elem = {
              id: object.properties.cartodb_id,
              name: object.properties.name,
              address: object.properties.address
            }
            if (this.channel.pois.some(p => p.id === elem.id)) {
              this.channel.pois = this.channel.pois.filter(p => p.id !== elem.id)
            } else {
              this.channel.pois = this.channel.pois.concat(elem)
            }
          }
        })
      }
    },
    async updateAdvertLayer () {
      if (layerService.hasLayer(this.advertsLayerId)) {
        layerService.updateLayer(this.advertsLayerId, {
          updateTriggers: {
            getRadius: [this.channel.adverts, this.selectedAreaTab]
          }
        })
      } else {
        await layerService.addSQLLayer({
          id: this.advertsLayerId,
          sql: this.adverts.query,
          pickable: true,
          renderSubLayers: props => ([
            new PointRadiusLayer(props)
          ]),
          getFillColor: [22, 150, 157, 51],
          getLineColor: [22, 150, 157],
          iconSizeScale: 32,
          getRadius: d => {
            const id = d.properties.cartodb_id
            const selected = this.selectedAreaTab && this.channel.adverts.some(p => p.id === id)
            return (selected && this.selectedAreaTab.radius) || 0
          },
          getIcon: (d) => ({
            width: 40,
            height: 40,
            url: d.properties.advert_type === ADVERT_TYPES.PRODUCT ? advertIconUrl : advertSquareIconUrl
          }),
          getPosition: d => d.geometry.coordinates,
          getTooltip ({ object }) {
            const text = object.properties.name || object.properties.address
            const html = `
              <div>
                <p>
                  <small style="font-size: 80%; font-weight: 600">${object.properties.advert_type}</small>
                </p>
                <p>${text}</p>
              </div>
            `
            const style = {
              'background': 'var(--colorNegative)',
              color: 'var(--colorHigh)'
            }
            return { html, style, className: 'map-popup-white' }
          },
          updateTriggers: {
            getRadius: [this.channel.adverts, this.selectedAreaTab]
          },
          onClick: this.readonly ? undefined : ({ object }) => {
            const elem = {
              id: object.properties.cartodb_id,
              name: object.properties.name || object.properties.address,
              address: object.properties.address
            }
            if (this.channel.adverts.some(p => p.id === elem.id)) {
              this.channel.adverts = this.channel.adverts.filter(p => p.id !== elem.id)
            } else {
              this.channel.adverts = this.channel.adverts.concat(elem)
            }
          }
        })
      }
    },
    async updateZipcodeLayer () {
      if (layerService.hasLayer(this.zoneLayersId)) {
        layerService.updateLayer(this.zoneLayersId, {
          updateTriggers: {
            getFillColor: [this.hoveredZipcodeId, this.channelZones, this.selectedAreaTab]
          }
        })
      } else {
        await layerService.addSQLLayer({
          id: this.zoneLayersId,
          sql: this.zones.query,
          zIndex: -1,
          pickable: true,
          getLineColor: ZIPCODE_BORDER_COLOR,
          getFillColor: (object) => {
            const id = object.properties.cartodb_id
            const hovered = id === this.hoveredZipcodeId
            const selected = this.isInterest || this.isYoutube ||
              this.allZonesSelected ||
              this.channelZones.find(z => z.id === id)

            if (selected) {
              return ZIPCODE_SELECTED_COLOR
            }
            if (hovered) {
              return ZIPCODE_HOVER_COLOR
            }
            return ZIPCODE_COLOR
          },
          getTooltip ({ object }) {
            const text = `CP ${object.properties.code}`
            const style = {
              'background': 'var(--colorNegative)',
              color: 'var(--colorHigh)'
            }
            return { text, style, className: 'map-popup-white' }
          },
          updateTriggers: {
            getFillColor: [this.hoveredZipcodeId, this.channelZones, this.selectedAreaTab]
          },
          onHover: ({ object }) => {
            this.hoveredZipcodeId = (!this.readonly && (!this.isInterest || !this.isYoutube) && object && object.properties.cartodb_id) || null
          },
          onClick: (this.readonly || this.isInterest || this.isYoutube) ? undefined : ({ object }) => {
            const elem = {
              id: object.properties.cartodb_id,
              name: object.properties.name,
              code: object.properties.code
            }
            if (this.channelZones.some(z => z.id === elem.id)) {
              this.channel.zones = this.channelZones.filter(z => z.id !== elem.id)
            } else {
              this.channel.zones = this.channelZones.concat(elem)
            }
          }
        })
      }
    },
    onAreaTabSelected ({ entity, radius }) {
      this.centerMap(entity)
      this.toggleLayer(entity)
      this.selectedAreaTab = { entity, radius }
      if (!this.areasReady) {
        this.$nextTick(() => {
          this.areasReady = true
        })
      }
    },
    onChannelChange (isChannelChanged) {
      this.isChannelChanged = isChannelChanged
    },
    toggleBoxSelection (flag) {
      if (flag) {
        this.enableBoxSelection()
      } else {
        this.disableBoxSelection()
      }
    },
    enableBoxSelection () {
      this.boxSelectionEnabled = true
      layerService.deckInstance.setProps({
        controller: false,
        onDragStart: ({ x, y }) => {
          this.dragStart = { x, y }
        },
        onDrag: ({ x, y }) => {
          this.dragCurrent = { x, y }
        },
        onDragEnd: ({ x, y }) => {
          if (!this.hasBoxSelection) {
            return
          }
          const minX = Math.min(this.dragStart.x, x)
          const minY = Math.min(this.dragStart.y, y)
          const maxX = Math.max(this.dragStart.x, x)
          const maxY = Math.max(this.dragStart.y, y)

          const isZone = this.selectedAreaTab.entity === 'zone'
          const layerId = isZone ? this.zoneLayersId : this.poisLayerId
          const original = isZone ? this.channel.zones : this.channel.pois
          const selection = layerService.deckInstance.pickObjects({
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY,
            layerIds: [layerId]
          })
            .filter(info => info.object && (
              !original.some(g => g.id === info.object.properties.cartodb_id)
            ))
            .map(info => ({
              id: info.object.properties.cartodb_id,
              name: info.object.properties.name
            }))

          const fullSelection = original.concat(selection)
          if (isZone) {
            this.channel.zones = fullSelection
          } else {
            this.channel.pois = fullSelection
          }

          this.dragStart = null
          this.dragCurrent = null
        }
      })
    },
    disableBoxSelection () {
      this.boxSelectionEnabled = false
      layerService.deckInstance.setProps({
        controller: true,
        onDragStart: null,
        onDrag: null,
        onDragEnd: null
      })
    },
    saveNewTemplate () {
      const body = {
        name: this.channel.newTemplateName,
        products: this.channel.products.map(p => p.id),
        target: this.channel.target.id
      }

      return templatesService.createTemplate(body)
    },
    saveDirectCreativities () {
      let promise
      if (this.channel.direction === CHANNEL_PRODUCT_TYPES.HYBRID.value) {
        promise = channelService.uploadFilesHybrid({
          isNew: this.channelid === 'new',
          openingUrl: this.channel.opening_creativity_url,
          closingUrl: this.channel.closing_creativity_url,
          openingFiles: this.channel.opening_files,
          closingFiles: this.channel.closing_files,
          id: this.channel.id,
          type: this.type
        })
      }
      if (this.channel.direction === CHANNEL_PRODUCT_TYPES.DIRECT.value) {
        promise = channelService.uploadFiles({
          isNew: this.channelid === 'new',
          url: this.channel.creativity_url,
          files: this.channel.files,
          id: this.channel.id,
          type: this.type
        })
      }
      return promise
    },
    async saveChannel () {
      this.loading = true
      if (this.channel.saveAsTemplate) {
        try {
          const newTemplate = await this.saveNewTemplate()
          this.channel.saveAsTemplate = false
          this.channel.newTemplateName = null
          this.channel.template = newTemplate
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('[channel-sidebar.js] Error saving new template', err)
          this.loading = false
          return
        }
      }

      const templateId = this.channel.template ? this.channel.template.id : null
      const startDate = this.channel.start_date
      const finishDate = this.channel.finish_date

      const customerRegions = this.areas.map(a => ({
        id: a.id,
        radius: this.selectedAreaTab.radius
      }))

      const isZone = this.selectedAreaTab.entity === 'zone'
      const zones = this.isInterest || this.isYoutube ? ['all'] : this.channel.zones.map(z => z.id)
      const products = this.channel.products.map(p => (
        p.url_overriden ? { id: p.id, url: p.url } : p.id
      ))

      let body = {
        id: this.channel.id,
        campaign: this.campaign.id,
        name: `${CHANNEL_LABELS[this.type]} - ${this.campaign.name}`,
        channel_type: this.type,
        campaign_start_date: this.campaign.start_date,
        campaign_finish_date: this.campaign.finish_date,
        budget: Number(this.channel.budget),
        start_date: new Date(startDate).toISOString().split('T')[0],
        finish_date: new Date(finishDate).toISOString().split('T')[0],
        selected_format: this.channel.format
          ? this.channel.format.id
          : formatOptions[0].id,
        template: templateId === 'new' ? null : templateId,
        direction: this.channel.direction,
        products,
        target: this.channel.target.type === 'THIRD_PARTY' ? [this.channel.target.id] : this.channel.target.identifiers.map(i => i.id),
        comment: this.channel.comment || '_',
        customer_regions: isZone ? [] : customerRegions,
        regions: isZone ? zones : [],
        cta: this.channel.cta && this.channel.cta.id,
        geo_template_id: this.channel.geo_template_id
      }

      if (body.channel_type === CHANNEL_OPTIONS.YOUTUBE) {
        body = {
          ...body,
          purchase_type: this.channel.purchase_type,
          video_length: this.channel.video_length,
          video_url: this.channel.video_url
        }
      }

      try {
        this.templateUpdateError = null
        const channel = await channelService.saveChannel(body, this.campaign)
        if (!this.channel.id) this.channel.id = channel.id
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[channel-sidebar.js] Error saving channel', err)
        this.loading = false

        const is400 = err && err.response && err.response.status === 400
        if (is400 && err.response.data.message === TEMPLATE_UPDATE_TIME_ERROR) {
          this.templateUpdateError = err.response.data
        }

        return
      }

      if (this.channel.direction !== CHANNEL_PRODUCT_TYPES.REDIRECT.value) {
        try {
          await this.saveDirectCreativities()
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('[channel-sidebar.js] Error saving files', err)
          this.loading = false
          return
        }
      }

      this.loading = false
      this.kpisDirty = false
      this.areasReady = false
      this.isChannelChanged = false

      this.$router.replace({ path: `/campaigns/${this.campaignid}/${this.channel.id}/${this.type}` })
    }
  }
}
